/**
 * Корневой роутер.
 *
 * Страницы разделены по уровню доступа:
 * - публичные: лендинг, вход, оверлей;
 * - приватные: dashboard, настройки, устройства, история, аккаунт.
 *
 * Оверлей и трекер загружаются лениво: OBS открывает только `/overlay/:token`,
 * и тянуть туда код личного кабинета ни к чему.
 */

import { Suspense, lazy } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { AppShell } from './components/AppShell';
import { LoadingScreen } from './components/LoadingScreen';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';

const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const RouteSettingsPage = lazy(() =>
  import('./pages/RouteSettingsPage').then((m) => ({ default: m.RouteSettingsPage })),
);
const OverlaySettingsPage = lazy(() =>
  import('./pages/OverlaySettingsPage').then((m) => ({ default: m.OverlaySettingsPage })),
);
const DevicesPage = lazy(() =>
  import('./pages/DevicesPage').then((m) => ({ default: m.DevicesPage })),
);
const HistoryPage = lazy(() =>
  import('./pages/HistoryPage').then((m) => ({ default: m.HistoryPage })),
);
const AccountPage = lazy(() =>
  import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })),
);
const OverlayPage = lazy(() =>
  import('./pages/OverlayPage').then((m) => ({ default: m.OverlayPage })),
);
const TrackerPage = lazy(() =>
  import('./pages/TrackerPage').then((m) => ({ default: m.TrackerPage })),
);

/**
 * Гвард приватных страниц.
 *
 * Текущий путь передаётся в `returnTo`, чтобы после входа вернуть
 * пользователя туда, куда он шёл, а не всегда на dashboard.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen label="Проверяем сессию" />;

  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <>{children}</>;
}

/** Страница входа не нужна тому, кто уже авторизован. */
function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen label="Проверяем сессию" />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingScreen label="Загружаем интерфейс" />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />

            <Route
              path="/login"
              element={
                <RedirectIfAuthed>
                  <LoginPage />
                </RedirectIfAuthed>
              }
            />

            {/* Публичный оверлей: без AppShell и с прозрачным фоном. */}
            <Route path="/overlay/:token" element={<OverlayPage />} />

            {/* Источник GPS на телефоне: авторизуется токеном устройства,
                поэтому доступен без сессии. */}
            <Route path="/tracker" element={<TrackerPage />} />

            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/settings/route" element={<RouteSettingsPage />} />
              <Route path="/settings/overlay" element={<OverlaySettingsPage />} />
              <Route path="/settings/devices" element={<DevicesPage />} />
              <Route path="/history" element={<HistoryPage />} />
              <Route path="/account" element={<AccountPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
